/* eslint-disable camelcase */
import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Accordion, AccordionTab } from 'primereact/accordion';
import { Button } from 'primereact/button';
import { toast } from 'react-toastify';
import { useUpdateLayoutMutation } from '../coachingActionSlice';
import { toLocalDateString } from '../../../utils/dateUtils';
import {
    FrameSection,
    UnderstandSection,
    ExploreSection,
    LayoutSection
} from './components';
import {
    useFuelNotes,
    useLayoutForm,
    useActionData,
    useSessionData,
    validateLayoutForm,
    saveActionPlan
} from './hooks/useCoachStep';
import { CoachingHistory } from '../coachingHistory/CoachingHistory';
import './CoachStep.scss';
import CoachingGlidePath from '../../../components/coachingActionPlan/coachingGlidePath/CoachingGlidePath';
import { useCalculateNextBestActionAssociateMutation, useUpdateSessionReviewMutation } from '../coachingActionPlanSlice';
import { useCreateCoachingToDoMutation } from '../../../components/CoachingToDo/CoachingToDoSlice';
import { CoachingEntityTypes } from '../../../utils/typeConstants';
import { datetimeToDate } from '../../../utils/dateFormat';
import { useCAPAutoSave } from '../hooks/useCAPAutoSave';

interface CoachStepProps {
    sessionId?: number;
    metricName?: string;
    metricCurrentValue?: number | null;
    metricTypeId?: number;
    metricTypeName?: string;
    CAPSessionData?: any;
    coachingActionPlanId?: number;
    coachingNextBestActionId?: number;
    scorecardMetricId?: number;
    // When true: entering from a TYPE_REVIEW NBA record (plan already Open/actioned).
    // Hides "Save Draft" and "Start Action Plan"; shows "Save Action Plan" instead.
    isReviewSession?: boolean;
    onCancel?: () => void;
    onGoToPreviousStep?: () => void;
}
interface SessionData {
    frameNotes: string;
    understandNotes: string;
    exploreNotes: string;
    coachingSkillId: number | null;
    coachingBehaviorId: number | null;
    coachingActionId: number | null;
    actionDescription: string;
    successCriteria: string;
    notes: string;
    performanceCommitment: number | null;
    followUpDate: string | null;
}

export const CoachStep: React.FC<CoachStepProps> = ({
    sessionId,
    metricName,
    metricCurrentValue,
    metricTypeId,
    metricTypeName,
    CAPSessionData,
    coachingActionPlanId,
    coachingNextBestActionId,
    scorecardMetricId,
    isReviewSession = false,
    onCancel,
    onGoToPreviousStep,
}) => {
    const [showValidation, setShowValidation] = useState(false);

    const fuelNotes = useFuelNotes();
    const layoutForm = useLayoutForm();

    const { coachingActions } = useActionData(layoutForm.behaviorId);

    const [updateLayout, { isLoading: isUpdating }] = useUpdateLayoutMutation();
    const [updateSessionReview, { isLoading: isSavingReview }] = useUpdateSessionReviewMutation();
    const [calculateNextBestAction] = useCalculateNextBestActionAssociateMutation();
    const [createToDo] = useCreateCoachingToDoMutation();

    const navigate = useNavigate();
    const location = useLocation();

    const currentSession = CAPSessionData?.sessions?.find(
        (s: any) => s.coachingActionPlanSessionId === sessionId
    );

    // ── Load existing session data ────────────────────────────────────────────
    // setFollowUpDate and setSessionDataLoaded are included so that:
    //   - an existing follow_up_date is restored instead of being overridden
    //     by the default "next Monday" logic in useLayoutForm
    //   - the default is only applied after we know the session has no saved date
    useSessionData(sessionId, metricTypeName, {
        setFrameNotes: fuelNotes.setFrameNotes,
        setUnderstandNotes: fuelNotes.setUnderstandNotes,
        setExploreNotes: fuelNotes.setExploreNotes,
        setPerformanceCommitment: layoutForm.setPerformanceCommitment,
        setFollowUpDate: layoutForm.setFollowUpDate,
        setSkillId: layoutForm.setSkillId,
        setBehaviorId: layoutForm.setBehaviorId,
        setActionId: layoutForm.setActionId,
        setActionDescription: layoutForm.setActionDescription,
        setSuccessCriteria: layoutForm.setSuccessCriteria,
        setNotes: layoutForm.setNotes,
        setSessionDataLoaded: layoutForm.setSessionDataLoaded,
    });

    useEffect(() => {
        sessionStorage.setItem('cap_nav_intent', 'true');
        navigate(location.pathname, {
            replace: true,
            state: { ...location.state, activeStep: 2 }
        });
    }, []);

    const handleSkillChange = (value: number) => {
        layoutForm.setSkillId(value);
        layoutForm.setBehaviorId(null);
        layoutForm.setActionId(null);
    };

    const handleBehaviorChange = (value: number) => {
        layoutForm.setBehaviorId(value);
        layoutForm.setActionId(null);
    };

    const handleCancel = () => {
        if (onCancel) onCancel();
        else toast.info('Action plan cancelled');
    };

    const getPerformanceCommitment = (value: any) => {
        if (value == null) return null;
        const parsed = parseFloat(value.toString());
        return metricTypeName === 'Percentage' ? parsed / 100 : parsed;
    };

    const buildSessionPayload = (data: any) => ({
        coaching_action_plan_session_id: sessionId,
        coaching_action_plan_id: coachingActionPlanId,
        frame_notes: data?.frameNotes,
        understand_notes: data?.understandNotes,
        explore_notes: data?.exploreNotes,
        coaching_skill_id: data?.coachingSkillId ?? null,
        coaching_behavior_id: data?.coachingBehaviorId ?? null,
        coaching_action_id: data?.coachingActionId ?? null,
        action_description: data?.actionDescription,
        success_criteria: data?.successCriteria,
        notes: data?.notes,
        performance_commitment: getPerformanceCommitment(data?.performanceCommitment),
        follow_up_date: data?.followUpDate
            ? toLocalDateString(new Date(data.followUpDate))
            : null,
    });

    const saveSession = async (data: any) => {
        await updateSessionReview(buildSessionPayload(data)).unwrap();
        await calculateNextBestAction({
            scorecardVersionId: CAPSessionData?.scorecardVersionId,
            employeeNumber: CAPSessionData?.employeeNumber,
        }).unwrap();
    };

    const handleSaveDraft = async () => {
        if (!sessionId || !coachingActionPlanId) {
            toast.error('No session available to save');
            return;
        }
        try {
            await saveSession({
                frameNotes: fuelNotes.frameNotes,
                understandNotes: fuelNotes.understandNotes,
                exploreNotes: fuelNotes.exploreNotes,
                coachingSkillId: layoutForm.skillId,
                coachingBehaviorId: layoutForm.behaviorId,
                coachingActionId: layoutForm.actionId,
                actionDescription: layoutForm.actionDescription,
                successCriteria: layoutForm.successCriteria,
                notes: layoutForm.notes,
                performanceCommitment: layoutForm.performanceCommitment,
                followUpDate: layoutForm.followUpDate,
            });
            toast.success('Draft saved successfully');
        } catch {
            toast.error('Failed to save draft');
        }
    };

    // ── Save Action Plan (Review session only) ────────────────────────────────
    const handleSaveActionPlan = async () => {
        if (!sessionId || !coachingActionPlanId) {
            toast.error('No session available to save');
            return;
        }
        try {
            await saveSession({
                frameNotes: fuelNotes.frameNotes,
                understandNotes: fuelNotes.understandNotes,
                exploreNotes: fuelNotes.exploreNotes,
                coachingSkillId: layoutForm.skillId,
                coachingBehaviorId: layoutForm.behaviorId,
                coachingActionId: layoutForm.actionId,
                actionDescription: layoutForm.actionDescription,
                successCriteria: layoutForm.successCriteria,
                notes: layoutForm.notes,
                performanceCommitment: layoutForm.performanceCommitment,
                followUpDate: layoutForm.followUpDate,
            });
            toast.success('Action plan saved successfully');
            if (onCancel) onCancel();
        } catch {
            toast.error('Failed to save action plan');
        }
    };
    const createCoachingToDo = async () => {
        const todoStartDate = new Date();
        const todoDueDate = new Date();
        todoDueDate.setDate(todoDueDate.getDate() + 3);
        createToDo({
            employeeNumbers: [CAPSessionData?.employeeNumber],
            createBy: CAPSessionData?.authorEmployeeNumber,
            coachingEntityTypeId: CoachingEntityTypes.CoachingActionPlan,
            coachingEntityId: coachingActionPlanId,
            title: 'Please Acknowledge Coaching Action Plan - ' + metricName + '',
            description:
                'Please approve the Coaching Action Plan for metric ' +
                metricName +
                ', created on ' +
                (CAPSessionData?.coachingStartDate ? datetimeToDate(CAPSessionData.coachingStartDate) : '') +
                '. Open the CAP by navigating to Coaching -> Action Plans. Then, review the plan and click Acknowledge.',
            startDate: datetimeToDate(todoStartDate),
            dueDate: datetimeToDate(todoDueDate),
            coachingStatusId: 1,
        })
            .unwrap()
            .then((res: any) => {
                if (res.data) {
                    toast.success('To Do created successfully.');
                }
            })
            .catch(() => {
                toast.error('To Do creation failed.');
            });
    };

    const handleStartActionPlan = async () => {
        setShowValidation(true);

        const isValid = validateLayoutForm(
            layoutForm.skillId,
            layoutForm.behaviorId,
            layoutForm.actionId,
            layoutForm.performanceCommitment
        );

        if (!isValid) return;

        if (!sessionId) {
            toast.error('No session ID available');
            return;
        }

        const success = await saveActionPlan(
            sessionId,
            coachingActionPlanId,
            coachingNextBestActionId,
            scorecardMetricId,
            {
                frameNotes: fuelNotes.frameNotes,
                understandNotes: fuelNotes.understandNotes,
                exploreNotes: fuelNotes.exploreNotes,
            },
            {
                skillId: layoutForm.skillId!,
                behaviorId: layoutForm.behaviorId!,
                actionId: layoutForm.actionId!,
                actionDescription: layoutForm.actionDescription,
                successCriteria: layoutForm.successCriteria,
                notes: layoutForm.notes,
                // Divide ÷ 100 to convert user-facing percentage back to decimal fraction for storage
                performanceCommitment: metricTypeName === 'Percentage'
                    ? parseFloat(layoutForm.performanceCommitment) / 100
                    : parseFloat(layoutForm.performanceCommitment),
                followUpDate: layoutForm.followUpDate,
            },
            fuelNotes.saveFuelNotes,
            updateLayout
        );

        if (success) {
            createCoachingToDo();
            setShowValidation(false);
            if (onCancel) onCancel();
        }
    };

    const { updateField } = useCAPAutoSave<SessionData>({
        data: currentSession,
        onSave: async (updatedData) => {
            if (!sessionId || !coachingActionPlanId) return;
            try {
                await saveSession(updatedData);
                // toast.success('Auto saved successfully.');
            } catch (error) {
                console.error('UpdateSessionReview failed:', error);
                toast.error('Failed to save auto save.');
            }
        },
        idleTimeout: 15000,
    });

    return (
        <div className="coach-step-container">
            <CoachingGlidePath pageType="action-plan" CAPSession={CAPSessionData} />

            <div className="coach-step-layout">
                <div className="coach-step-layout__history">
                    <CoachingHistory coachingActionPlanId={coachingActionPlanId ?? 0}
                        CAPSessionData={CAPSessionData}
                    />
                </div>

                <div className="coach-step-layout__content">
                    <Accordion multiple activeIndex={[0]}>
                        <AccordionTab
                            header={
                                <div className="fuel-accordion-header">
                                    <span className="fuel-icon">F</span>
                                    <span>Frame</span>
                                </div>
                            }
                        >
                            <FrameSection
                                frameNotes={fuelNotes.frameNotes}
                                // onFrameNotesChange={fuelNotes.setFrameNotes}
                                onFrameNotesChange={(val) => {
                                    fuelNotes.setFrameNotes(val);
                                    updateField('frameNotes', val);
                                }}
                            />
                        </AccordionTab>

                        <AccordionTab
                            header={
                                <div className="fuel-accordion-header">
                                    <span className="fuel-icon">U</span>
                                    <span>Understand</span>
                                </div>
                            }
                        >
                            <UnderstandSection
                                understandNotes={fuelNotes.understandNotes}
                                onUnderstandNotesChange={(val) => {
                                    fuelNotes.setUnderstandNotes(val);
                                    updateField('understandNotes', val);
                                }}
                            />
                        </AccordionTab>

                        <AccordionTab
                            header={
                                <div className="fuel-accordion-header">
                                    <span className="fuel-icon">E</span>
                                    <span>Explore</span>
                                </div>
                            }
                        >
                            <ExploreSection
                                exploreNotes={fuelNotes.exploreNotes}
                                onExploreNotesChange={(val) => {
                                    fuelNotes.setExploreNotes(val);
                                    updateField('exploreNotes', val);
                                }}
                            />
                        </AccordionTab>

                        <AccordionTab
                            header={
                                <div className="fuel-accordion-header">
                                    <span className="fuel-icon">L</span>
                                    <span>Layout</span>
                                </div>
                            }
                        >
                            <LayoutSection
                                metricName={metricName ?? ''}
                                metricCurrentValue={metricCurrentValue ?? null}
                                metricTypeId={metricTypeId}
                                metricTypeName={metricTypeName}
                                performanceCommitment={layoutForm.performanceCommitment}
                                onPerformanceCommitmentChange={(val) => {
                                    layoutForm.setPerformanceCommitment(val);
                                    updateField('performanceCommitment', val);
                                }}
                                followUpDate={layoutForm.followUpDate}
                                onFollowUpDateChange={(val) => {
                                    layoutForm.setFollowUpDate(val);
                                    updateField('followUpDate', val);
                                }}
                                skillId={layoutForm.skillId}
                                onSkillChange={(val) => {
                                    handleSkillChange(val);
                                    updateField('coachingSkillId', val);
                                }}
                                behaviorId={layoutForm.behaviorId}
                                onBehaviorChange={(val) => {
                                    handleBehaviorChange(val);
                                    updateField('coachingBehaviorId', val);
                                }}
                                actionId={layoutForm.actionId}
                                onActionChange={(val) => {
                                    layoutForm.setActionId(val);
                                    updateField('coachingActionId', val);
                                }}
                                actionDescription={layoutForm.actionDescription}
                                onActionDescriptionChange={(val) => {
                                    layoutForm.setActionDescription(val);
                                    updateField('actionDescription', val);
                                }}
                                successCriteria={layoutForm.successCriteria}
                                onSuccessCriteriaChange={(val) => {
                                    layoutForm.setSuccessCriteria(val);
                                    updateField('successCriteria', val);
                                }}
                                notes={layoutForm.notes}
                                onNotesChange={(val) => {
                                    layoutForm.setNotes(val);
                                    updateField('notes', val);
                                }}
                                coachingActions={coachingActions}
                                showValidation={showValidation}
                                CAPCurrentSession={currentSession}
                                onBeforeOverride={handleSaveDraft}
                            />
                        </AccordionTab>
                    </Accordion>

                    {/* ── Action Buttons ──────────────────────────────────────────────────────
                        Review session  → Cancel | Go to Previous Step | Save Action Plan
                        Normal session  → Cancel | Save Draft | Go to Previous Step | Start Action Plan
                    */}
                    <div className="coach-actions">
                        <Button
                            label="Cancel"
                            icon="pi pi-times-circle"
                            className="btn-secondary"
                            onClick={handleCancel}
                        />
                        <Button
                            label="Go to Previous Step"
                            icon="pi pi-check-circle"
                            className="btn-secondary"
                            onClick={onGoToPreviousStep}
                            disabled={isUpdating}
                        />

                        {isReviewSession ? (
                            <Button
                                label="Save Action Plan"
                                icon="pi pi-check-circle"
                                className="btn-primary"
                                onClick={handleSaveActionPlan}
                                loading={isSavingReview}
                            />
                        ) : (
                            <>
                                <Button
                                    label="Save Draft"
                                    icon="pi pi-check-circle"
                                    className="btn-secondary"
                                    onClick={handleSaveDraft}
                                    disabled={isUpdating}
                                />
                                <Button
                                    label="Start Action Plan"
                                    icon="pi pi-arrow-circle-right"
                                    className="btn-primary"
                                    onClick={handleStartActionPlan}
                                    loading={isUpdating}
                                />
                            </>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};
